import React, {
  useEffect, useRef, useState,
} from 'react';
import {motion, useInView, useReducedMotion} from 'motion/react';
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
} from '@heroicons/react-v2/24/outline';
import {useAppContext} from '../../hooks';

export const primaryButton = 'inline-flex items-center justify-center gap-2.5 rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-[0_3px_0_#4b13b6,0_6px_20px_rgba(110,43,240,0.18)] transition hover:-translate-y-0.5 hover:bg-indigo-700 motion-reduce:transition-none motion-reduce:hover:translate-y-0';
export const secondaryButton = 'inline-flex items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-white px-6 py-3 text-base font-semibold text-gray-900 transition hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-indigo-50/50 motion-reduce:transition-none motion-reduce:hover:translate-y-0';
export const textLink = 'group inline-flex items-center gap-2 text-base font-semibold text-indigo-600 transition hover:text-indigo-800';
export const eyebrow = 'text-xs font-semibold uppercase tracking-[0.08em] text-gray-400';

/** Arrow that nudges right on hover of the surrounding `textLink`. */
export const LinkArrow = () => (
  <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
);

/**
 * Scroll-triggered entrance: content below the fold fades and rises as it
 * scrolls into view. The hidden state is only applied after hydration, so
 * server-rendered HTML is never hidden from crawlers or no-JS visitors.
 */
export const Reveal = ({children, className, delay = 0}) => {
  const reducedMotion = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, {once: true, amount: 0.15, margin: '0px 0px -8% 0px'});
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (!reducedMotion && rect && rect.top > window.innerHeight) setArmed(true);
  }, [reducedMotion]);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={false}
      animate={armed && !inView ? {opacity: 0, y: 48} : {opacity: 1, y: 0}}
      transition={{
        duration: 0.9, delay, ease: [0.22, 1, 0.36, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

export const SectionHeading = ({eyebrow: eyebrowText, title, description}) => (
  <div className="mx-auto max-w-2xl text-center">
    {eyebrowText && <span className={eyebrow}>{eyebrowText}</span>}
    <h2 className="mt-4 text-balance text-4xl font-semibold tracking-tight text-gray-900 md:text-5xl">
      {title}
    </h2>
    {description && (
      <p className="mx-auto mt-5 max-w-xl text-pretty text-lg leading-relaxed text-gray-600">
        {description}
      </p>
    )}
  </div>
);

const companies = [
  {name: 'Google', src: 'wordmarks/google.svg'},
  {name: 'Meta', src: 'wordmarks/meta.svg'},
  {name: 'Amazon', src: 'wordmarks/amazon.svg'},
  {name: 'Microsoft', src: 'wordmarks/microsoft.svg'},
  {name: 'Apple', src: 'wordmarks/apple.svg'},
  {name: 'Airbnb', src: 'wordmarks/airbnb.svg'},
  {name: 'Stripe', src: 'wordmarks/stripe.svg'},
  {name: 'DoorDash', src: 'doordash_wordmark.png'},
  {name: 'OpenAI', src: 'openai.png'},
  {name: 'TikTok', src: 'tiktok.png'},
  {name: 'Spotify', src: 'spotify.png'},
];

export const CompanyFeed = () => (
  <section className="py-10 md:py-16" aria-label="Companies we help you prepare for">
    <p className="mb-8 text-center text-sm font-medium text-gray-600 md:text-base">
      We’ve helped people land jobs at top companies
    </p>
    <div className="overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_12%,black_88%,transparent)]">
      <div className="flex w-max animate-[marquee-x_52s_linear_infinite] motion-reduce:animate-none">
        {[0, 1].map((copy) => (
          <div className="flex items-center gap-16 pr-16 md:gap-20 md:pr-20" key={copy} aria-hidden={copy === 1}>
            {companies.map(({name, src}) => (
              <div className="flex h-10 shrink-0 items-center justify-center" key={name}>
                <img
                  src={`/images/companies/${src}`}
                  alt={copy === 0 ? name : ''}
                  className="h-6 w-auto max-w-32 object-contain md:h-7"
                  loading="eager"
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Testimonial = ({item}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-6">
      <blockquote
        className={`text-pretty text-[15px] leading-relaxed text-gray-600 ${expanded ? '' : 'line-clamp-6'}`}
      >
        {item.text}
      </blockquote>
      {item.text?.length > 240 && (
        <button
          className="mt-2.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Read full review'}
        </button>
      )}
      <div className="mt-5 flex items-center gap-3">
        {item.image && (
          <img
            className="h-10 w-10 rounded-full object-cover"
            src={item.image}
            alt=""
            loading="lazy"
          />
        )}
        <div className="min-w-0">
          <strong className="block truncate text-sm font-semibold text-gray-900">
            {item.name}
          </strong>
          <small className="block truncate text-xs text-gray-500">
            {[item.title || item.role, item.company].filter(Boolean).join(' · ')}
          </small>
        </div>
      </div>
    </article>
  );
};

export const Community = () => {
  const {testimonials = []} = useAppContext();
  const rail = useRef(null);
  const reducedMotion = useReducedMotion();
  // Same selection as the old homepage: every featured testimonial.
  const featured = testimonials.filter((t) => t.featured);
  const items = featured.length ? featured : testimonials.slice(0, 16);
  const scroll = (direction) => rail.current?.scrollBy({
    left: direction * 360,
    behavior: reducedMotion ? 'instant' : 'smooth',
  });

  // Pack up to three cards per column by estimated height, so short quotes
  // stack instead of leaving dead space under two-card columns.
  const estimateHeight = (item) => {
    const lines = Math.min(Math.ceil((item.text?.length || 0) / 42), 6);
    return 120 + lines * 24;
  };
  const columns = items.reduce((cols, item) => {
    const last = cols[cols.length - 1];
    const height = estimateHeight(item);
    if (last && last.items.length < 3 && last.height + height <= 560) {
      last.items.push(item);
      last.height += height;
    } else {
      cols.push({items: [item], height});
    }
    return cols;
  }, []);
  return (
    <section className="pb-4 pt-12 md:pb-6 md:pt-24">
      <div className="mx-auto w-full max-w-6xl px-5">
        <Reveal className="flex items-end justify-between gap-6">
          <div>
            <span className={eyebrow}>TESTIMONIALS</span>
            <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
              Loved by over 1 million people
            </h2>
            <p className="mt-4 text-pretty text-lg leading-relaxed text-gray-600">
              Since 2018, we’ve been helping people land their dream jobs.
            </p>
          </div>
          <div className="hidden shrink-0 gap-2.5 sm:flex">
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-indigo-50"
              aria-label="Previous testimonials"
              onClick={() => scroll(-1)}
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-indigo-50"
              aria-label="Next testimonials"
              onClick={() => scroll(1)}
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
      {items.length > 0 && (
        <div
          className="scrollbar-hidden mt-10 flex items-start gap-5 overflow-x-auto px-5 pb-6 md:px-[max(20px,calc((100vw-1152px)/2))]"
          ref={rail}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          role="region"
          aria-label="Community testimonials. Scroll to read more."
        >
          {columns.map(({items: columnItems}) => (
            <div
              className="flex w-80 shrink-0 flex-col gap-5 md:w-[340px]"
              key={columnItems[0].id || columnItems[0].name}
            >
              {columnItems.map((item) => (
                <Testimonial item={item} key={item.id || item.name} />
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 flex justify-center">
        <a className={textLink} href="/reviews">
          Read more testimonials
          <LinkArrow />
        </a>
      </div>
    </section>
  );
};

const agents = [
  {name: 'Claude', src: '/images/agents/claude.svg'},
  {name: 'ChatGPT', src: '/images/agents/openai.svg'},
  {name: 'Muse', src: '/images/companies/icons/meta.svg'},
];

export const AgentSection = () => (
  <section className="mx-auto w-full max-w-6xl px-5">
    <Reveal className="relative overflow-hidden rounded-3xl border border-gray-800 bg-gray-950 p-8 py-12 sm:p-12 md:py-16">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.08),transparent_55%)]"
        aria-hidden="true"
      />
      <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.07]" aria-hidden="true">
        <filter id="agent-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#agent-grain)" />
      </svg>
      <div className="relative mx-auto flex max-w-xl flex-col items-center text-center">
        <div className="flex items-center">
          {agents.map(({name, src}, i) => (
            <span
              key={name}
              title={name}
              className={`flex h-14 w-14 items-center justify-center rounded-full border border-gray-700 bg-gray-900 shadow-lg ${i > 0 ? '-ml-3' : ''}`}
            >
              <img src={src} alt={name} className="h-6 w-6 object-contain" />
            </span>
          ))}
          <span
            className="-ml-3 flex h-14 w-14 items-center justify-center rounded-full border border-gray-700 bg-gray-800 text-lg font-medium text-gray-400 shadow-lg"
            aria-hidden="true"
          >
            +
          </span>
        </div>
        <h2 className="mt-6 text-balance text-3xl font-semibold tracking-tight text-white md:text-4xl">
          Use Aced with your favorite agent.
        </h2>
        <p className="mt-4 max-w-md text-pretty text-base leading-relaxed text-gray-300">
          Connect Claude, ChatGPT, Muse, or any AI assistant to Aced through
          MCP for automated job search and interview prep.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-5">
          <a
            className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-white px-6 py-3 text-base font-semibold text-gray-900 transition hover:-translate-y-0.5 hover:bg-indigo-50 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            href="/signup?src=homepage-mcp"
          >
            Join the waitlist
            <ArrowRightIcon className="h-4 w-4" />
          </a>
        </div>
      </div>
    </Reveal>
  </section>
);

const plans = [
  {
    title: 'Free',
    description: 'Try sample lessons, browse questions, and join the community. No credit card required.',
    features: [
      'Explore free course lessons',
      'Browse interview questions',
      'Join the Aced community',
    ],
    cta: 'Create your free account',
    href: '/signup?src=homepage-pricing',
  },
  {
    title: 'Membership',
    description: 'Full courses, expert answers, and unlimited mock interviews with monthly and annual plans.',
    features: [
      'Full interview prep courses',
      'Expert answers and rubrics',
      'Unlimited mock interviews',
    ],
    cta: 'Explore plans and pricing',
    href: '/upgrade?src=homepage-pricing',
    highlighted: true,
  },
  {
    title: 'Enterprise',
    description: 'Bring Aced to your school, bootcamp, or organization with dedicated support.',
    features: [
      'Group platform access',
      'Analytics and insights',
      'Dedicated training & support',
    ],
    cta: 'Learn more',
    href: '/pages/university?src=homepage-pricing',
  },
];

export const Pricing = () => (
  <section id="pricing" className="mx-auto w-full max-w-6xl scroll-mt-24 px-5 pb-14 pt-10 md:pb-28 md:pt-16">
    <SectionHeading
      title="Pricing"
      description="Start for free, upgrade when you’re ready."
    />
    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {plans.map((plan, i) => (
        <Reveal
          key={plan.title}
          delay={i * 0.1}
          className={`flex flex-col rounded-2xl bg-white p-8 ${
            plan.highlighted
              ? 'border-2 border-indigo-300 shadow-xl shadow-indigo-950/5'
              : 'border border-gray-200'
          }`}
        >
          <h3 className="text-3xl font-semibold tracking-tight text-gray-900">
            {plan.title}
          </h3>
          <p className="mt-3 text-pretty text-base leading-relaxed text-gray-600">
            {plan.description}
          </p>
          <ul className="mb-8 mt-6 space-y-3">
            {plan.features.map((text) => (
              <li key={text} className="flex items-start gap-3 text-base text-gray-600">
                <CheckIcon className="mt-1 h-4.5 w-4.5 shrink-0 text-indigo-500" />
                {text}
              </li>
            ))}
          </ul>
          <a
            href={plan.href}
            className={`${plan.highlighted ? primaryButton : secondaryButton} mt-auto`}
          >
            {plan.cta}
            <ArrowRightIcon className="h-4 w-4" />
          </a>
        </Reveal>
      ))}
    </div>
  </section>
);
